import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowRight, Copy, FileText, History, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { et } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";

export type CollisionDecision = "skip" | "duplicate" | "version";

export interface CollisionPair {
  /** Stable key to identify this pair. Use the file object identity via a caller-provided id. */
  id: string;
  /** Incoming file the user selected for upload. */
  incomingFile: File;
  /** The existing document's current latest filename (usually identical to incomingFile.name). */
  existingFilename: string;
  /** Current version of the existing document. */
  existingVersion: number;
  /** Version group UUID, if the existing doc has one. */
  versionGroup: string | null;
  /** Byte size of the existing document, if known. */
  existingSize?: number | null;
  /** Most recent timestamp for the existing document (ISO-8601). */
  existingModifiedAt?: string | null;
  /** Display name of the uploader of the existing document. */
  existingUploaderName?: string | null;
}

interface UploadCollisionDialogProps {
  open: boolean;
  pairs: CollisionPair[];
  /** Called when the user confirms — map of pair id to chosen decision. */
  onResolve: (decisions: Record<string, CollisionDecision>) => void;
  /** Called when the user aborts the whole batch. */
  onCancel: () => void;
}

/** Tokens matching the "Variant A · Cleanup" design canvas. Kept in one
 *  block so anyone reskinning the dialog has a single source of truth. */
const TONE = {
  /** Tinted badge behind the header icon. */
  headerIconBg: "bg-[#fff5f3] text-primary",
  /** Accent for the "Duplicate" action (Franklin accent brown). */
  duplicateFg: "text-[#a5815d]",
  duplicateDot: "bg-[#a5815d]",
  /** Selected-row highlight (very light pink). */
  rowSelectedBg: "bg-[#fcf7f6]",
  /** Version action uses the app's primary red via shadcn tokens. */
  versionFg: "text-primary",
  versionDot: "bg-primary",
  skipFg: "text-muted-foreground",
  skipDot: "bg-muted-foreground",
} as const;

function formatBytes(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * UploadCollisionDialog — Variant A.
 *
 * Pop-up that lets a user decide, per filename-collision, whether to create a
 * New Version, Keep Both (duplicate), or Skip the incoming file. Supports
 * multi-select + bulk-apply via a persistent toolbar, and shows a live tally
 * of the outcome next to the primary button.
 *
 * The default decision is `version` — by the time the dialog fires, the user
 * has already dropped a file into an existing folder; versioning is the most
 * common intent.
 */
export function UploadCollisionDialog({
  open,
  pairs,
  onResolve,
  onCancel,
}: UploadCollisionDialogProps) {
  const { t, language } = useLanguage();
  const dateLocale = language === "et" ? et : undefined;
  const tr = t.documents.versioning.collisionDialog;

  const [decisions, setDecisions] = useState<Record<string, CollisionDecision>>(() =>
    Object.fromEntries(pairs.map((p) => [p.id, "version" as const]))
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // Reset on batch change.
  const pairKey = useMemo(() => pairs.map((p) => p.id).join("|"), [pairs]);
  useEffect(() => {
    setSelectedIds(new Set());
    setDecisions(Object.fromEntries(pairs.map((p) => [p.id, "version" as const])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairKey]);

  const allSelected = pairs.length > 0 && selectedIds.size === pairs.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < pairs.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(pairs.map((p) => p.id)));
  };
  const togglePair = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Per-row pick — if this pair is part of a multi-select, apply to all
   *  selected rows; otherwise set only this row. Mirrors the design copy
   *  ("Picking an action on a selected row applies to all selected rows"). */
  const pickDecision = (pairId: string, decision: CollisionDecision) => {
    setDecisions((prev) => {
      const next = { ...prev };
      if (selectedIds.has(pairId) && selectedIds.size > 1) {
        for (const id of selectedIds) next[id] = decision;
      } else {
        next[pairId] = decision;
      }
      return next;
    });
  };

  /** Bulk-apply from the top toolbar. If any rows are selected we apply to
   *  just those; otherwise we treat it as "apply to all". */
  const bulkApply = (decision: CollisionDecision) => {
    setDecisions((prev) => {
      const next = { ...prev };
      const targets = selectedIds.size > 0 ? selectedIds : new Set(pairs.map((p) => p.id));
      for (const id of targets) next[id] = decision;
      return next;
    });
  };

  const handleConfirm = () => onResolve(decisions);

  // Outcome tally used both in the footer and as the primary button's count.
  const tally = useMemo(() => {
    const counts = { version: 0, duplicate: 0, skip: 0 } as Record<CollisionDecision, number>;
    for (const p of pairs) counts[decisions[p.id] ?? "version"] += 1;
    return counts;
  }, [pairs, decisions]);
  const uploadCount = pairs.length - tally.skip;

  const actions: {
    value: CollisionDecision;
    label: string;
    short: string;
    tooltip: string;
    icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
    activeFg: string;
  }[] = [
    {
      value: "version",
      label: tr.actions.version.label,
      short: tr.actions.version.short,
      tooltip: tr.actions.version.tooltip,
      icon: History,
      activeFg: TONE.versionFg,
    },
    {
      value: "duplicate",
      label: tr.actions.duplicate.label,
      short: tr.actions.duplicate.short,
      tooltip: tr.actions.duplicate.tooltip,
      icon: Copy,
      activeFg: TONE.duplicateFg,
    },
    {
      value: "skip",
      label: tr.actions.skip.label,
      short: tr.actions.skip.short,
      tooltip: tr.actions.skip.tooltip,
      icon: X,
      activeFg: TONE.skipFg,
    },
  ];

  const bulkTarget =
    selectedIds.size > 0
      ? tr.bulk.nSelected.replace("{count}", String(selectedIds.size))
      : tr.bulk.applyToAll;

  const primaryLabel =
    uploadCount === 0
      ? tr.confirmNone
      : uploadCount === 1
        ? tr.confirmSingle
        : tr.confirmMany.replace("{count}", String(uploadCount));

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onCancel() : undefined)}>
      <DialogContent className="max-w-[95vw] gap-0 overflow-hidden p-0 sm:max-w-6xl">
        {/* Header */}
        <DialogHeader className="space-y-1 border-b px-7 pb-4 pt-6 text-left">
          <DialogTitle className="flex items-center gap-2.5 text-base font-semibold tracking-tight">
            <span
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md",
                TONE.headerIconBg
              )}
            >
              <History className="h-4 w-4" strokeWidth={2} />
            </span>
            {pairs.length === 1
              ? tr.titleSingle
              : tr.titleMany.replace("{count}", String(pairs.length))}
          </DialogTitle>
          <DialogDescription className="pl-[38px] text-[13px] leading-relaxed">
            {tr.subtitle}
          </DialogDescription>
        </DialogHeader>

        {/* Bulk toolbar */}
        <div className="flex items-center gap-3 border-b bg-muted/30 px-7 py-2.5">
          <label className="flex cursor-pointer items-center gap-2.5 text-xs font-medium text-foreground">
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={toggleSelectAll}
              aria-label={tr.selectAllAria}
              className="h-[15px] w-[15px] rounded-[3px]"
            />
            {selectedIds.size > 0
              ? tr.bulk.nSelected.replace("{count}", String(selectedIds.size))
              : tr.bulk.selectAll.replace("{count}", String(pairs.length))}
          </label>
          <div className="flex-1" />
          <span className="text-[11px] text-muted-foreground">
            {tr.bulk.applyTo.replace("{target}", bulkTarget)}
          </span>
          <div className="flex items-center gap-1.5">
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => bulkApply(a.value)}
                  className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <Icon className="h-3 w-3" strokeWidth={1.8} />
                  {a.short}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rows */}
        <TooltipProvider delayDuration={150}>
          <div className="max-h-[540px] overflow-y-auto">
            {pairs.map((pair, index) => {
              const decision = decisions[pair.id] ?? "version";
              const isSelected = selectedIds.has(pair.id);

              const existingSize = formatBytes(pair.existingSize);
              const incomingSize = formatBytes(pair.incomingFile.size);
              const sizeLine =
                existingSize && incomingSize
                  ? tr.row.sizeDiff
                      .replace("{oldSize}", existingSize)
                      .replace("{newSize}", incomingSize)
                  : incomingSize
                    ? tr.row.sizeSingle.replace("{size}", incomingSize)
                    : null;

              let timeLine: string | null = null;
              if (pair.existingModifiedAt) {
                try {
                  const rel = formatDistanceToNow(new Date(pair.existingModifiedAt), {
                    addSuffix: true,
                    locale: dateLocale,
                  });
                  timeLine = pair.existingUploaderName
                    ? tr.row.uploaderLine
                        .replace("{relTime}", rel)
                        .replace("{uploader}", pair.existingUploaderName)
                    : tr.row.uploaderLineNoName.replace("{relTime}", rel);
                } catch {
                  timeLine = null;
                }
              }

              return (
                <div
                  key={pair.id}
                  className={cn(
                    "grid grid-cols-[auto_1fr_auto] items-center gap-4 px-7 py-3.5 transition-colors",
                    index < pairs.length - 1 && "border-b",
                    isSelected ? TONE.rowSelectedBg : "bg-background"
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => togglePair(pair.id)}
                    aria-label={tr.selectRowAria.replace("{filename}", pair.incomingFile.name)}
                    className="h-[15px] w-[15px] rounded-[3px]"
                  />

                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText
                        className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground"
                        strokeWidth={1.8}
                      />
                      <span
                        className="truncate text-[13px] font-medium text-foreground"
                        title={pair.incomingFile.name}
                      >
                        {pair.incomingFile.name}
                      </span>
                    </div>
                    <div className="ml-[22px] flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span>
                        {tr.row.existingMeta.replace("{version}", String(pair.existingVersion))}
                      </span>
                      {timeLine && (
                        <>
                          <span className="text-border">·</span>
                          <span>{timeLine}</span>
                        </>
                      )}
                      {sizeLine && (
                        <>
                          <span className="text-border">·</span>
                          <span>{sizeLine}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Segmented action radio */}
                  <div className="inline-flex rounded-md border bg-muted p-0.5" role="radiogroup">
                    {actions.map((a) => {
                      const active = decision === a.value;
                      const Icon = a.icon;
                      return (
                        <Tooltip key={a.value}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() => pickDecision(pair.id, a.value)}
                              className={cn(
                                "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11.5px] transition-all",
                                active
                                  ? cn("bg-background font-semibold shadow-sm", a.activeFg)
                                  : "font-medium text-muted-foreground hover:text-foreground"
                              )}
                            >
                              <Icon
                                className="h-[11px] w-[11px]"
                                strokeWidth={active ? 2.2 : 1.8}
                              />
                              {a.short}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs">
                            {a.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </TooltipProvider>

        {/* Footer */}
        <footer className="flex items-center gap-3 border-t bg-background px-7 py-3.5">
          <Tally tally={tally} t={tr.tally} />
          <div className="flex-1" />
          {/* 36px page action — the upload control scale. Cancel follows the
              upload button so the pair stays the same height. */}
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t.common.cancel}
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={uploadCount === 0}
            className="gap-1.5"
          >
            {primaryLabel}
            {uploadCount > 0 && <ArrowRight className="h-3 w-3" strokeWidth={2} />}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

/** Live outcome tally: colored dots next to counts, inline with the footer. */
function Tally({
  tally,
  t: tr,
}: {
  tally: Record<CollisionDecision, number>;
  t: { newVersion: string; duplicate: string; skipped: string };
}) {
  const items: { key: CollisionDecision; label: string; dot: string }[] = [
    { key: "version", label: tr.newVersion, dot: TONE.versionDot },
    { key: "duplicate", label: tr.duplicate, dot: TONE.duplicateDot },
    { key: "skip", label: tr.skipped, dot: TONE.skipDot },
  ];
  const visible = items.filter((it) => tally[it.key] > 0);
  if (visible.length === 0) return <div />;

  return (
    <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
      {visible.map((it, i) => (
        <span key={it.key} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-border">·</span>}
          <span className={cn("h-1.5 w-1.5 rounded-full", it.dot)} />
          <span className="font-semibold text-foreground">{tally[it.key]}</span>
          <span>{it.label}</span>
        </span>
      ))}
    </div>
  );
}
